import type { FC, KeyboardEvent, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useId, useRef, useState } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'

export type MenuItem = {
    label: string
    description?: string
    icon?: ReactNode
    href?: string
    onSelect?: () => void
    tone?: 'default' | 'danger'
    checked?: boolean
    separatorBefore?: boolean
}

type DropdownMenuProps = {
    trigger: ReactNode
    label: string
    items: MenuItem[]
    align?: 'start' | 'end'
    side?: 'bottom' | 'top'
    triggerClassName?: string
    menuClassName?: string
    showLabel?: boolean
}

const ITEM_CLASSES = 'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] font-medium outline-none transition-colors [&_svg]:size-4 [&_svg]:shrink-0'

export const DropdownMenu: FC<DropdownMenuProps> = ({
    trigger,
    label,
    items,
    align = 'end',
    side = 'bottom',
    triggerClassName,
    menuClassName,
    showLabel = true
}): ReactNode => {
    const [open, setOpen] = useState(false)
    const rootRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const menuId = useId()

    useEffect(() => {
        if (!open) return

        const handlePointer = (event: MouseEvent): void => {
            if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
        }

        const handleKey = (event: globalThis.KeyboardEvent): void => {
            if (event.key === 'Escape') {
                setOpen(false)
                triggerRef.current?.focus()
            }
        }

        document.addEventListener('mousedown', handlePointer)
        document.addEventListener('keydown', handleKey)
        menuRef.current?.querySelector<HTMLElement>('[role^="menuitem"]')?.focus()

        return () => {
            document.removeEventListener('mousedown', handlePointer)
            document.removeEventListener('keydown', handleKey)
        }
    }, [open])

    const handleMenuKeys = (event: KeyboardEvent<HTMLDivElement>): void => {
        const nodes = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]') ?? [])
        const index = nodes.indexOf(document.activeElement as HTMLElement)

        if (event.key === 'ArrowDown') {
            event.preventDefault()
            nodes[(index + 1) % nodes.length]?.focus()
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault()
            nodes[(index - 1 + nodes.length) % nodes.length]?.focus()
        }

        if (event.key === 'Tab') setOpen(false)
    }

    const select = (item: MenuItem): void => {
        setOpen(false)
        item.onSelect?.()
    }

    return (
        <div ref={rootRef} className='relative'>
            <button
                ref={triggerRef}
                type='button'
                aria-haspopup='menu'
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                aria-label={showLabel ? undefined : label}
                onClick={() => setOpen(value => !value)}
                className={triggerClassName ?? buttonVariants({ variant: 'outline' })}
            >
                {trigger}
            </button>

            {open && (
                <div
                    ref={menuRef}
                    id={menuId}
                    role='menu'
                    aria-label={label}
                    onKeyDown={handleMenuKeys}
                    className={cn(
                        'absolute z-50 min-w-52 rounded-xl border bg-popover p-1.5 text-popover-foreground shadow-float animate-in fade-in-0 zoom-in-95 duration-150',
                        align === 'end' ? 'right-0' : 'left-0',
                        side === 'bottom' ? 'top-full mt-2 origin-top' : 'bottom-full mb-2 origin-bottom',
                        menuClassName
                    )}
                >
                    {items.map(item => {
                        const role = item.checked === undefined ? 'menuitem' : 'menuitemradio'
                        const classes = cn(
                            ITEM_CLASSES,
                            item.tone === 'danger'
                                ? 'text-destructive hover:bg-destructive/10 focus-visible:bg-destructive/10'
                                : 'text-foreground hover:bg-accent focus-visible:bg-accent [&_svg]:text-muted-foreground'
                        )

                        const content = (
                            <>
                                {item.icon}

                                <span className='min-w-0 flex-1'>
                                    <span className='block'>{item.label}</span>

                                    {item.description && (
                                        <span className='block text-xs font-normal text-muted-foreground'>
                                            {item.description}
                                        </span>
                                    )}
                                </span>

                                {item.checked && <Check className='text-primary!' aria-hidden='true' />}
                            </>
                        )

                        return (
                            <div key={item.label}>
                                {item.separatorBefore && <div className='-mx-1.5 my-1.5 h-px bg-border' role='separator' />}

                                {item.href ? (
                                    <Link href={item.href} role={role} className={classes} tabIndex={-1} onClick={() => setOpen(false)}>
                                        {content}
                                    </Link>
                                ) : (
                                    <button
                                        type='button'
                                        role={role}
                                        aria-checked={item.checked}
                                        tabIndex={-1}
                                        className={classes}
                                        onClick={() => select(item)}
                                    >
                                        {content}
                                    </button>
                                )}
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}
